import React, { useMemo } from 'react';
import moment from 'moment';
import countriesData from "../../Config/countries.json";

const MailBoxChatWindow = ({
    activeChat,
    messages,
    repliesMap,
    loadingReplies,
    sendingReply,
    newReplyText,
    setNewReplyText,
    onSendReply,
    onDeleteContact,
    onBackToList,
    onUserClick,
    user,
    t
}) => {

    const getCountryFlag = (countryCode) => {
        if (!countryCode) return "";
        const code = countryCode.toLowerCase();
        const found = countriesData.countries.find(c => c.value === code || c.alpha3?.toLowerCase() === code);
        return found ? found.icon : `/assets/flags/32x32/${code}.png`;
    };

    const parseDate = (dateStr) => {
        if (!dateStr) return 0;
        const formats = ["DD/MM/YYYY HH:mm:ss", "DD/MM/YYYY HH:mm", "YYYY-MM-DD HH:mm:ss", "YYYY-MM-DDTHH:mm:ss.SSSZ"];
        const parsed = moment(dateStr, formats, true);
        return parsed.isValid() ? parsed.valueOf() : new Date(dateStr).getTime() || 0;
    };

    const formatMsgClock = (dateStr) => {
        if (!dateStr) return "";
        const m = moment(dateStr, ["DD/MM/YYYY HH:mm:ss", "DD/MM/YYYY HH:mm", "YYYY-MM-DD HH:mm:ss", "YYYY-MM-DDTHH:mm:ss.SSSZ"]);
        return m.isValid() ? m.clone().locale('ar').format("hh:mm A") : dateStr;
    };

    const getSeparatorDateText = (dateStr) => {
        if (!dateStr) return "";
        const m = moment(dateStr, ["DD/MM/YYYY HH:mm:ss", "DD/MM/YYYY HH:mm", "YYYY-MM-DD HH:mm:ss", "YYYY-MM-DDTHH:mm:ss.SSSZ"]);
        if (!m.isValid()) return dateStr;

        const mAr = m.clone().locale('ar');
        if (m.isSame(moment(), 'day')) {
            return t("range.Today") || "اليوم";
        } else if (m.isSame(moment().subtract(1, 'day'), 'day')) {
            return t("range.Yesterday") || "الأمس";
        } else {
            return mAr.format("LL"); // Localized Arabic full date
        }
    };

    // Combine all incoming emails and outgoing replies into a chronological list
    const combinedMessages = useMemo(() => {
        const list = [];
        if (!messages) return list;

        messages.forEach(c => {
            // Add the contact message (incoming)
            list.push({
                id: `contact_${c.Id}`,
                realId: c.Id,
                type: 'incoming',
                title: c.Title,
                body: c.Details,
                date: c.CreationDate,
                timestamp: parseDate(c.CreationDate),
                sender: c.Sender,
                email: c.Email,
                country: c.Country,
                schoolName: c.FromUser?.School?.Name,
                userTypeName: c.FromUser?.UserType?.Name
            });

            // Add replies (outgoing)
            const replies = repliesMap[c.Id] || [];
            replies.forEach(r => {
                list.push({
                    id: `reply_${r.Id}`,
                    realId: r.Id,
                    type: 'outgoing',
                    body: r.Body,
                    date: r.CreationDate,
                    timestamp: parseDate(r.CreationDate),
                    sender: r.User?.Name || 'Admin'
                });
            });
        });

        // Sort: oldest first, newest at the bottom of the chat window
        return list.sort((a, b) => a.timestamp - b.timestamp);
    }, [messages, repliesMap]);

    const chatName = activeChat.name || activeChat.email;
    const initials = chatName.substring(0, 1).toUpperCase();
    const photoUrl = activeChat.photo;
    const countryCode = activeChat.country;

    const adminPhoto = user?.user?.Photo || null;
    const adminName = user?.user?.Name || 'Admin';
    const adminInitials = adminName.substring(0, 1).toUpperCase();

    return (
        <div className="whatsapp-main-chat">
            {/* Header */}
            <div className="whatsapp-chat-header">
                <div className="whatsapp-chat-header-info">
                    {onBackToList && (
                        <button
                            type="button"
                            className="btn btn-link p-0 ml-2 whatsapp-back-btn text-dark"
                            onClick={onBackToList}
                            title={t("Close") || "رجوع"}
                            style={{ fontSize: '20px', border: 'none', background: 'transparent', cursor: 'pointer' }}
                        >
                            <i className="fal fa-arrow-right"></i>
                        </button>
                    )}
                    <div
                        className="whatsapp-avatar clickable-avatar ml-2"
                        title={activeChat.user ? t("Pages.WhatsApp.showUserInfo") || "عرض معلومات المستخدم" : ""}
                        onClick={() => {
                            if (activeChat.user && onUserClick) {
                                onUserClick(activeChat.user);
                            }
                        }}
                    >
                        {photoUrl ? (
                            <img src={photoUrl} alt={chatName} className="whatsapp-avatar-img" />
                        ) : (
                            initials
                        )}
                    </div>
                    <div>
                        <h6
                            className="whatsapp-active-name"
                            style={{ cursor: activeChat.user ? 'pointer' : 'default' }}
                            onClick={() => {
                                if (activeChat.user && onUserClick) {
                                    onUserClick(activeChat.user);
                                }
                            }}
                        >
                            {chatName}
                            {countryCode && (
                                <img
                                    src={process.env.PUBLIC_URL + getCountryFlag(countryCode)}
                                    className="country-flag-icon"
                                    title={countryCode.toUpperCase()}
                                    alt={countryCode.toUpperCase()}
                                    onError={(e) => { e.target.style.display = 'none'; }}
                                />
                            )}
                            {activeChat.user?.UserType && (
                                <span className="mailbox-user-type-badge">
                                    {activeChat.user.UserType.Name}
                                </span>
                            )}
                            {activeChat.user?.School && (
                                <span className="mailbox-school-badge">
                                    {activeChat.user.School.Name}
                                </span>
                            )}
                        </h6>
                        <p className="whatsapp-active-status text-muted small m-0">
                            {activeChat.email && (
                                <a href={`mailto:${activeChat.email}`} className="text-decoration-none text-muted mr-2">
                                    <i className="fal fa-envelope-open-text ml-1"></i>
                                    {activeChat.email}
                                </a>
                            )}
                            {activeChat.user?.Phone && (
                                <a href={`Tel:${activeChat.user.Phone}`} className="text-decoration-none text-muted">
                                    <i className="fal fa-phone ml-1"></i>
                                    {activeChat.user.Phone}
                                </a>
                            )}
                        </p>
                    </div>
                </div>
                <div className="whatsapp-sidebar-actions">
                    <button
                        className="whatsapp-icon-btn text-danger"
                        title={t("Pages.MailBox.action.remove") || "حذف المحادثة"}
                        onClick={() => {
                            if (messages && messages.length > 0) {
                                // Delete latest message as representative of removing current conversation node
                                onDeleteContact(messages[messages.length - 1].Id);
                            }
                        }}
                    >
                        <i className="fal fa-trash-alt"></i>
                    </button>
                </div>
            </div>

            {/* Messages Area */}
            <div className="whatsapp-messages-area" style={{ display: 'flex', flexDirection: 'column' }}>
                {loadingReplies ? (
                    <div className="text-center py-5 my-auto">
                        <i className="fa-spin fas fa-spinner text-primary h3"></i>
                    </div>
                ) : combinedMessages.length === 0 ? (
                    <div className="text-center py-5 my-auto text-muted">
                        <p>{t("NoInfo")}</p>
                    </div>
                ) : (() => {
                    let lastDay = null;
                    return combinedMessages.map((msg) => {
                        const mDate = moment(msg.date, ["DD/MM/YYYY HH:mm:ss", "DD/MM/YYYY HH:mm", "YYYY-MM-DD HH:mm:ss", "YYYY-MM-DDTHH:mm:ss.SSSZ"]);
                        const dayString = mDate.isValid() ? mDate.format("YYYY-MM-DD") : "";
                        const showSeparator = dayString !== lastDay;
                        if (dayString) lastDay = dayString;

                        const isOutgoing = msg.type === "outgoing";
                        return (
                            <React.Fragment key={msg.id}>
                                {showSeparator && dayString && (
                                    <div className="mailbox-date-separator">
                                        <span>{getSeparatorDateText(msg.date)}</span>
                                    </div>
                                )}
                                <div className={`whatsapp-message-wrapper ${isOutgoing ? "outgoing" : "incoming"}`} style={{ display: 'flex', alignItems: 'flex-end', gap: '8px', margin: '8px 0' }}>
                                    {isOutgoing && (
                                        <div className="whatsapp-avatar mr-2" style={{ width: '28px', height: '28px', fontSize: '11px', flexShrink: 0 }}>
                                            {adminPhoto ? (
                                                <img src={adminPhoto} alt={adminName} className="whatsapp-avatar-img" />
                                            ) : (
                                                adminInitials
                                            )}
                                        </div>
                                    )}
                                    <div className="whatsapp-message-bubble" style={{ position: 'relative', margin: 0 }}>
                                        {/* Subject Title for incoming email */}
                                        {msg.title && (
                                            <div className="mailbox-message-title" style={{ fontWeight: 'bold', marginBottom: '6px' }}>
                                                <strong> {msg.title}</strong>
                                            </div>
                                        )}

                                        {/* Message Body */}
                                        <div
                                            className="mailbox-message-content"
                                            dangerouslySetInnerHTML={{ __html: msg.body }}
                                        />

                                        {/* Time of message at the bottom right/left corner */}
                                        <span className="whatsapp-message-time">
                                            {formatMsgClock(msg.date)}
                                        </span>
                                    </div>
                                    {!isOutgoing && (
                                        <div className="whatsapp-avatar ml-2" style={{ width: '28px', height: '28px', fontSize: '11px', flexShrink: 0 }}>
                                            {photoUrl ? (
                                                <img src={photoUrl} alt={chatName} className="whatsapp-avatar-img" />
                                            ) : (
                                                initials
                                            )}
                                        </div>
                                    )}
                                </div>
                            </React.Fragment>
                        );
                    });
                })()}
            </div>

            {/* Input Bar */}
            <form className="whatsapp-input-bar" onSubmit={onSendReply}>
                <textarea
                    rows={1}
                    className="whatsapp-input-field"
                    placeholder={t("Pages.WhatsApp.typeMessagePlaceholder") || "اكتب ردًا..."}
                    value={newReplyText}
                    onChange={(e) => setNewReplyText(e.target.value)}
                    disabled={sendingReply}
                    style={{ resize: 'none', borderRadius: '16px', padding: '6px 10px', lineHeight: '18px' }}
                    onKeyDown={(e) => {
                        // Enter sends message, Shift+Enter inserts new line
                        if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            if (newReplyText.trim() && !sendingReply) {
                                onSendReply(e);
                            }
                        }
                    }}
                />
                <button
                    type="submit"
                    className="whatsapp-icon-btn"
                    disabled={!newReplyText.trim() || sendingReply}
                    title={t("Pages.MailBox.send") || "إرسال"}
                >
                    {sendingReply ? (
                        <i className="fa-spin fas fa-spinner text-primary"></i>
                    ) : (
                        <i className="fal fa-paper-plane" style={{ transform: 'scaleX(-1)' }}></i>
                    )}
                </button>
            </form>
        </div>
    );
};

export default MailBoxChatWindow;
